import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, combineLatest, merge, Observable, ReplaySubject, Subject, throwError, timer } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	first,
	map,
	retry,
	share,
	shareReplay,
	switchMap,
	tap,
	throttleTime,
	timeout,
} from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { hasKeys, pickTruthy } from 'Util';
import { AuthenticationService } from './authentication.service';
import { FeatureFlagService, FLAGS } from './feature-flag.service';
import { HttpService } from './http-service';
import { AudibleEvent, EventData, LiveUpdateEvent } from './live-update.service.types';

export * from './live-update.service.types';

function doesFilterMatch(prefix: string, path: string): boolean {
	const prefixParts: string[] = prefix.split('.');
	const pathParts: string[] = path.split('.');

	if (prefixParts.length > pathParts.length) {
		return false;
	}

	for (let i = 0; i < prefixParts.length; i++) {
		if (prefixParts[i] !== pathParts[i]) {
			return false;
		}
	}

	return true;
}

/**
 * This returns an exponential back-off with a random value added,
 * in case every user gets disconnected at the same time (like when
 * a back-end deployment causes a general disconnect).
 * The number returned will start at `min` milliseconds, and then increase exponentially by
 * the power of 2, with a random number of milliseconds below `min` added.
 * This will decrease the load on the server if everyone is trying to reconnect at once,
 * because their connections will be slightly staggered.
 * @remarks
 * Note: if issues are seen in the future, may want to increase the random number added
 * (change Math.random() * 1000 to Math.random() * 2000, for example).
 * This will max out at `max` milliseconds and stop incrementing the value.
 */
function getExponentalBackoff(min: number, max: number, failedCount: number) {
	return Math.min(Math.pow(2, failedCount) * min + Math.floor(Math.random() * min), max);
}

interface RawMessageData<D extends EventData | Event = EventData | Event> {
	action: string;
	detail?: string;
	data?: D;
	audible_event?: AudibleEvent;
}

@Injectable({
	providedIn: 'root',
})
export class LiveUpdateService {
	private authenticated$ = new Subject();
	private close$ = new Subject();

	/**
	 * An observable that emits its current value on subscribe and whenever the connection state changes.
	 *This is bound to authenticated$ and close$ in the constructor.
	 */
	isConnected$ = new BehaviorSubject(true);

	// controls how long to wait for a message from the server before considering the connection
	// dead. The server sends a heartbeat message every 10 seconds, with a write deadline of 10
	// seconds. In total the server could take up to 20 seconds to send a heartbeat message.
	private static MESSAGE_DELAY_GRACE_PERIOD = 40_000;

	// controls how long to wait for the server to successfully identify
	// the user. Must be less than `MESSAGE_DELAY_GRACE_PERIOD`.
	private static INITIAL_IDENTIFY_TIMEOUT = 10_000;

	private createWebSocket(url: string) {
		return webSocket({
			url: url,
			openObserver: {
				next: () => {
					console.debug(`websocket opened with url: ${url}`);
				},
			},
			closeObserver: this.close$,
		});
	}

	private static getWebSocketUrls(
		serverUrl: string,
		useNewWSURL: boolean
	): {
		primary: string;
		fallback: string | null;
	} {
		if (!useNewWSURL) {
			return {
				primary: serverUrl,
				fallback: null,
			};
		}

		const mapping = environment.websocket.urlMappings.find((mapping) => mapping.originalUrl === serverUrl);
		if (mapping) {
			return {
				primary: mapping.newUrl,
				fallback: mapping.originalUrl,
			};
		}

		// If no mapping found, use the original URL
		return {
			primary: serverUrl,
			fallback: null,
		};
	}

	private websocketConfig$$ = combineLatest([this.authenticationService.server$, this.featureFlagService.isFeatureEnabledV2$(FLAGS.NewWSURL)]).pipe(
		map(([server, useNewWSURL]) => {
			if (!server) return null;

			return LiveUpdateService.getWebSocketUrls(server.ws_url, useNewWSURL);
		}),
		pickTruthy(),
		distinctUntilChanged((prev, curr) => {
			return prev?.primary === curr?.primary && prev?.fallback === curr?.fallback;
		}),
		map(({ primary, fallback }) => {
			return {
				primarySocket: this.createWebSocket(primary),
				fallbackSocket: fallback ? this.createWebSocket(fallback) : null,
				primaryUrl: primary,
				fallbackUrl: fallback,
			};
		}),
		// Sharing the websocket object here prevents us from making duplicate websocket connections.
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	private forceReconnect$ = new BehaviorSubject<void>(undefined);

	private identifyUser(
		socket: WebSocketSubject<unknown>,
		spCookie: string,
		schoolId: number,
		isFallback = false
	): Observable<WebSocketSubject<unknown>> {
		// buffer an authenticate message before any other messages
		socket.next({
			action: 'authenticate',
			token: spCookie,
			token_type: 'cookie_value',
			school_id: schoolId,
		});

		return socket.pipe(
			tap((msg) => console.debug(`received event while waiting for successful authentication (${isFallback ? 'fallback' : 'primary'}): `, msg)),
			filter((msg: unknown): boolean => {
				return hasKeys<{ action: string }>(msg, ['action']) && msg.action === 'authenticate.success';
			}),
			first(), // Take only the first authenticate.success message
			// Then continue with the original websocket stream
			// This ensures we've received auth confirmation before processing other messages
			// and considering the connection live.
			tap(() => {
				this.authenticated$.next(true);
				console.debug(`websocket authenticated successfully using ${isFallback ? 'fallback' : 'primary'} connection`);
			}),
			// After auth success, return to the original websocket stream
			map(() => socket)
		);
	}

	private _identifiedSocket$ = new ReplaySubject<WebSocketSubject<unknown>>(1);

	private websocket$$ = this.forceReconnect$.pipe(
		throttleTime(2000, undefined, { leading: true, trailing: true }),
		switchMap(() =>
			combineLatest([this.websocketConfig$$, this.httpService.currentSchool$.pipe(pickTruthy(), distinctUntilKeyChanged('id'))]).pipe(
				// This ad-hoc cookie fetch is necessary to ensure that a cookie value is set,
				// which is usually the case whenever currentSchool emits.
				map((args) => [...args, this.cookieService.get('smartpassToken')] as const),
				switchMap(([sockets, school, spCookie]) => {
					// First try the primary connection
					return this.identifyUser(sockets.primarySocket, spCookie, school.id, false).pipe(
						timeout({
							each: LiveUpdateService.INITIAL_IDENTIFY_TIMEOUT,
							with: () => throwError(() => new Error(`primary connection attempt timed out`)),
						}),
						// If primary fails and we have a fallback, switch to the fallback
						catchError((error) => {
							console.warn('Primary WebSocket connection failed:', error);
							if (sockets.fallbackSocket) {
								console.debug('Attempting fallback WebSocket connection');
								return this.identifyUser(sockets.fallbackSocket, spCookie, school.id, true);
							}
							return throwError(() => new Error('Primary connection failed and no fallback available'));
						})
					);
				})
			)
		),
		// This is a known bad pattern, but we don't have a great way to get access to the currently indentified socket in a pure RxJS way.
		// The problem is that we only want one subscription to websocket$$ and we want to know its last emission in sendMessage.
		tap((socket) => this._identifiedSocket$.next(socket))
	);

	private events$ = this.websocket$$.pipe(
		switchMap((socket) => socket), // The inner observable is the messages on the socket
		timeout({
			each: LiveUpdateService.MESSAGE_DELAY_GRACE_PERIOD,
			with: ({ seen }) =>
				throwError(
					() => new Error(`more than ${LiveUpdateService.MESSAGE_DELAY_GRACE_PERIOD}ms have elapsed since last message. ${seen} messages received`)
				),
		}),
		retry({
			delay: (error, retryCount) => {
				console.debug('websocket error', error);
				const duration = getExponentalBackoff(1_000, 30_000, retryCount);
				console.debug('restarting connection in ms', duration);
				return timer(duration);
			},
			resetOnSuccess: true,
		}),
		share({
			resetOnError: false, // should never happen given the above retry
			resetOnComplete: true,
			resetOnRefCountZero: false, // keep connection open regardless of number of subscribers
		})
	);

	private messages$ = this.events$.pipe(
		// TODO: better validation
		filter((event): event is RawMessageData<EventData> => hasKeys(event, ['action']))
	);

	constructor(
		private httpService: HttpService,
		private cookieService: CookieService,
		private authenticationService: AuthenticationService,
		private featureFlagService: FeatureFlagService
	) {
		merge(this.authenticated$.pipe(map(() => true)), this.close$.pipe(map(() => false))).subscribe(this.isConnected$);
	}

	forceReconnect() {
		this.forceReconnect$.next(undefined);
	}

	listen(filterString?: string): Observable<LiveUpdateEvent> {
		return this.messages$.pipe(filter((data) => !filterString || doesFilterMatch(filterString, data.action)));
	}

	listenOnCurrentSchool(filterString?: string): Observable<LiveUpdateEvent> {
		return this.listen(filterString).pipe(
			filter((e) => {
				const school = this.httpService.getSchool();
				return !school || !hasKeys(e?.data, ['school_id']) || e?.data?.school_id === school.id;
			})
		);
	}

	/** @deprecated: Don't send messages back to the server */
	sendMessage(action: string, data?: string | null) {
		this._identifiedSocket$.pipe(first()).subscribe((socket) => {
			socket.next({ action, data });
		});
	}

	restartOnConnected() {
		return <T>(source: Observable<T>): Observable<T> =>
			merge(
				source,
				this.isConnected$.pipe(
					filter(Boolean),
					switchMap(() => source)
				)
			);
	}
}
